import { cx, dateFormat } from '@/utils/strings'
import colors from '@/constants/colors'

import ImageContentful from '@/components/ui/image-contentful'
import DynamicAction from '@/components/ui/dynamic-action'
import { getLocale } from '@/utils/locale'
import { usePage } from '@inertiajs/react'
import { buttonStyles } from './ui/cta-link'
import DownloadPdfButton from './ui/download-pdf-button'
import { getBackground, checkOverideDarkMode } from '@/utils/backgrounds'
import gradients from '@/constants/gradients'
import Link from '@/components/link-component'

const AuthorAvatarWrapper = ({ children, author }) => {
  return author?.fields?.slug ? (
    <div>
      <Link
        to={`/blog/author/${author.fields.slug}/`}
        className="no-underline hover:no-underline inline-block"
        aria-label={`View author profile`}
      >
        {children}
      </Link>
    </div>
  ) : (
    <>{children}</>
  )
}

export type ArticleHeaderProps = {
  author?: any
  backgroundColor?: string
  description?: string
  downloadAsPdf?: boolean
  image: any
  imageDarkMode: any
  imageOverlay?: string
  publishDate: any
  tagline?: string
  title: string
  fullWidthHero?: boolean
  heroButtons?: any[]
}

export default function ArticleHeader({
  author,
  backgroundColor = 'Space',
  description,
  downloadAsPdf,
  heroButtons,
  image,
  imageDarkMode,
  imageOverlay,
  publishDate,
  tagline,
  title,
}: ArticleHeaderProps) {
  const renderShield = () => <span className="font-bwi text-[160px]">&#xe932;</span>

  const page = usePage()
  const pathname = page.url

  const upperCaseAuthor = (authorName: string) => {
    const names = authorName.split(' ')
    return names.length > 1 ? (names[0].charAt(0) + names[1].charAt(0)).toUpperCase() : names[0].charAt(0).toUpperCase()
  }
  const isSvg = image?.fields?.file?.contentType === 'image/svg+xml'

  const determineBtn = () => {
    if (checkOverideDarkMode(backgroundColor) || backgroundColor === colors.white || imageOverlay === 'white') {
      return false
    }
    return true
  }

  return (
    <header
      className={cx(
        'relative',
        backgroundColor && getBackground(backgroundColor),
        imageOverlay === 'white' && 'text-secondary-900!',
        'py-5 sm:py-12 lg:py-20'
      )}
    >
      <div className={cx('flex mx-auto max-w-8xl px-5 lg:px-9')}>
        <div className={cx('z-10 relative md:pr-10', !isSvg && !imageOverlay && image ? 'md:w-2/3' : 'md:w-9/12')}>
          {tagline && <div className="text-sm sm:text-base opacity-70 uppercase tracking-widest">{tagline}</div>}
          <div className="lg:min-h-[120px] flex items-start flex-col md:mt-4">
            <h1 className="text-2xl sm:text-3xl mt-2 mb-4 md:my-0 md:text-[40px] md:leading-[1.2]">{title}</h1>
            {description && <p className="$1t-4">{description}</p>}
            {(downloadAsPdf || heroButtons) && (
              <div className="flex flex-wrap justify-center gap-6 my-6">
                {downloadAsPdf && (
                  <DownloadPdfButton
                    btnText="Download as PDF"
                    hasDarkBg={determineBtn()}
                    overideDarkMode={checkOverideDarkMode(backgroundColor) || imageOverlay === 'white'}
                    buttonStyle={buttonStyles.btnSolid}
                  />
                )}

                {heroButtons &&
                  Array.isArray(heroButtons) &&
                  heroButtons.map((action) => {
                    if (!action) return null
                    const type = action.sys.contentType.sys.id
                    const fields = {
                      type,
                      ...action.fields,
                    }
                    return (
                      <DynamicAction
                        hasDarkBg={determineBtn()}
                        overideDarkMode={checkOverideDarkMode(backgroundColor) || imageOverlay === 'white'}
                        key={action?.sys.id}
                        {...fields}
                        data-testid="card-cta"
                      />
                    )
                  })}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 md:gap-0 2xl:w-4/5">
            {author && (
              <AuthorAvatarWrapper author={author}>
                <div className="flex items-center md:space-x-5">
                  <div
                    className={cx(
                      'hidden rounded-full md:flex justify-center items-center text-lg h-14 w-14 bg-accent-teal text-primaryBlue dark:text-secondary-900'
                    )}
                  >
                    {author.fields?.authorName ? (
                      <ImageContentful
                        {...author.fields.authorImage.fields}
                        className="rounded-full border border-secondary-50"
                      />
                    ) : (
                      upperCaseAuthor(author)
                    )}
                  </div>
                  <div className={cx(imageOverlay === 'white' ? 'text-secondary-800' : 'text-secondary-50')}>
                    <span className={cx('text-sm block opacity-70 ')}>authored by:</span>
                    <span className="text-sm sm:text-base md:text-lg ">
                      {author.fields ? author.fields.authorName : author}
                    </span>
                  </div>
                </div>
              </AuthorAvatarWrapper>
            )}
            {publishDate && (
              <div>
                <span className="text-sm block opacity-70">posted :</span>
                <time>{dateFormat(publishDate, getLocale(pathname))}</time>
              </div>
            )}
          </div>
        </div>

        {!isSvg && imageOverlay && (
          <div>
            <div
              className={cx(
                'absolute w-full left-0 right-0 top-0 z-5',
                imageOverlay === 'black' &&
                  'bg-black/60 sm:bg-transparent sm:bg-linear-to-r sm:from-black/90 sm:to-black/0 h-full',
                imageOverlay === 'white' &&
                  'bg-white/60 sm:bg-transparent sm:bg-linear-to-r sm:from-white/95 sm:via-white/90 sm:via-40% sm:to-black/0 h-[101%]',

                imageOverlay === 'white' && 'h-full'
              )}
            />

            <div className="absolute w-full h-full left-0 right-0 top-0 bottom-0">
              <ImageContentful
                className={cx('bg-primaryBlue h-full w-full object-cover')}
                {...image?.fields}
                darkModeImage={imageDarkMode?.fields}
              />
            </div>
          </div>
        )}

        {(isSvg || (!isSvg && !imageOverlay && image)) && (
          <div
            className={cx(
              'hidden md:flex items-center justify-end',
              !isSvg && !imageOverlay ? 'md:w-1/3' : 'md:w-3/12'
            )}
          >
            <ImageContentful
              className={cx(
                '[&_svg]:text-transparent',
                image?.fields?.file?.contentType !== 'image/svg+xml' && 'rounded-lg'
              )}
              {...image?.fields}
              darkModeImage={imageDarkMode?.fields}
            />
          </div>
        )}
        {!image && (
          <div
            className={cx(
              backgroundColor === gradients.arctic && 'text-primary-500',
              backgroundColor === gradients.sea && 'text-white',
              backgroundColor === gradients.horizon && 'text-white',
              'md:w-3/12 flex items-center justify-center'
            )}
          >
            {renderShield()}
          </div>
        )}
      </div>
    </header>
  )
}
